import { useState, useMemo, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import MapProvider from '@alltrails/maps/components/MapProvider';
import { getListId } from '@alltrails/shared/utils/lists/listHelpers';
import { PageStrings } from '@alltrails/shared/utils/constants/pageStringHelpers';
import { GeoLocationProvider } from 'components/explore/GeoLocationProvider';
import SearchInsightsProvider from 'components/SearchInsightsProvider';
import ErrorFallback from 'components/shared/ErrorFallback';
import { Page, PageSource } from 'components/shared/Page/Page';
import useResizeListener from 'hooks/useResizeListener';
import { Context } from '@alltrails/shared/types/context';
import logError from 'utils/logError';
import CustomProvider from 'components/CustomProvider';
import SignupModal from 'components/SignupModal';
import useToggle from '@alltrails/shared/hooks/useToggle';
import { pathToRegexp } from 'path-to-regexp';
import useUser from 'hooks/useUser';
import { MapExpandProvider } from 'components/MapExpandControl/MapExpandProvider';
import useListItems from '../../hooks/useListItems';
import NewFullscreenSearchApp from './NewFullscreenSearchApp';

type Props = {
  context: Context;
  initialCustomList: any;
  initialMyLists: any;
  initialMyListItems: any;
  initialPage: string;
  initialUserListItems: any;
  userInfo: any;
  locationData: {
    city: {
      names: { [key: string]: any[] };
    };
    country: string;
    latLng: (string | number)[];
  };
};

const listAccessForAdminOrCurrentUser = ({ context, userInfo }: Props, page: string) => {
  if (context.adminUser && page === PageStrings.EXPLORE_COMPLETED_PAGE) {
    return true;
  }

  return userInfo && context && context.currentUser && context.currentUser.id == userInfo.id;
};

const SearchApp = (props: Props) => {
  const user = useUser();
  const [isOpen, toggle] = useToggle(!user && pathToRegexp('/:locale?/explore/map/new').test(__AT_DATA__.pathname));
  const [page, setPage] = useState(props.initialPage || PageStrings.EXPLORE_ALL_PAGE);
  const [isMobileWidth, setIsMobileWidth] = useState(props.context.mobileBrowser || false);
  const listId = useMemo(() => getListId(page, props.initialCustomList), [page, props.initialCustomList]);
  const belongsToCurrentUser = listAccessForAdminOrCurrentUser(props, page);
  const { listMethods, renderModal, listItems, lists, latestListAction, userListItems } = useListItems(
    {
      lists: props.initialMyLists,
      listItems: props.initialMyListItems,
      userListItems: props.initialUserListItems,
      belongsToCurrentUser: belongsToCurrentUser && listId // this decides whether or not to edit an individual users list
    },
    page
  );

  const updatePage = (newPage: string) => {
    setPage(newPage);
  };

  const retryErrorPage = () => {
    window.location.reload();
  };

  const onResize = useCallback(() => {
    setIsMobileWidth(window.innerWidth <= 767);
  }, []);

  useResizeListener({ onResize });

  return (
    <Page pageSource={PageSource.Explore} context={props.context}>
      <SignupModal
        isOpen={isOpen}
        onRequestClose={() => {
          window.location.assign(document.referrer);
        }}
        onSuccess={() => {
          toggle();
        }}
      />
      <GeoLocationProvider>
        <SearchInsightsProvider context={props.context} pageName={PageSource.Explore}>
          {renderModal()}
          <ErrorBoundary fallback={<ErrorFallback retryErrorPage={retryErrorPage} />} onError={logError}>
            <MapProvider>
              <MapExpandProvider>
                <NewFullscreenSearchApp
                  {...props}
                  isMobileWidth={isMobileWidth}
                  latestListAction={latestListAction}
                  lists={lists}
                  listItems={listItems}
                  page={page}
                  userListItems={userListItems}
                  listMethods={listMethods}
                  listId={listId}
                  belongsToCurrentUser={belongsToCurrentUser}
                  updatePage={updatePage}
                />
              </MapExpandProvider>
            </MapProvider>
          </ErrorBoundary>
        </SearchInsightsProvider>
      </GeoLocationProvider>
    </Page>
  );
};

export default function SearchAppWrapper(props: Props) {
  return (
    <CustomProvider>
      <SearchApp {...props} />
    </CustomProvider>
  );
}
